import {observer} from "mobx-react";
import React from "react";
import FontBody1 from "../../../components/fonts/font-body1/font-body1";
import {FontBody2} from "../../../components/fonts/font-body2/font-body2";
import {FONT_COLOR, FONT_VARIANT} from "../../../components/fonts/font-props";
import IconResetToDefault from "../../../components/icons/icon-reset-to-default/icon-reset-to-default";
import {BUTTON_VARIANTS} from "../../../components/inputs/button-variants";
import {Button} from "../../../components/inputs/button/button";
import {DROPDOWN_MENU_WIDTH, SelectDropdown} from "../../../components/inputs/select-dropdown/select-dropdown";
import {Toggle} from "../../../components/inputs/toggle/toggle";
import Card from "../../../components/layouts/card/card";
import DashTranslation from "../../../dashboard-translation/dashboard-translaton";
import useDashboardTranslation from "../../../dashboard-translation/hooks/dashboard-translation-hook";
import config from "../../../config";
import {FEATURES} from "../../../models/Plan";
import {AUTO_PROCESS_TRIGGER, AUTO_PROCESS_VALIDATED_QUALIFIER} from "../../../models/User";
import currentPortalStore from "../../../stores/current-portal-store";
import {
    AUTO_PROCESS_EXECUTE,
    autoProcessExecuteOptionsList,
    autoProcessTriggerOptionsList,
    autoProcessValidatedQualifierOptionsList,
    getAutoProcessSinceValueOptions,
    SINCE_UNIT,
    sinceUnitOptionsList
} from "../../policies/constants";
import './rma-automation-card.scss';
import rmaAutoRefundStore from "../stores/rma-auto-refund-store";
import RmaRequestStore from "../stores/rma-request-store";
import { IValidationGroup } from "../../../hooks/validation-group/validation-group-hook";


interface IProps {
	rmaRequestStore: RmaRequestStore;
	validationGroup: IValidationGroup;
}

const RMAAutomationCard: React.FC<IProps> = observer(({ rmaRequestStore, validationGroup }) => {
	const rmaAutoRefundStore: rmaAutoRefundStore = rmaRequestStore.autoRefundStore;
	const autoProcessSinceValueOptions = getAutoProcessSinceValueOptions(rmaAutoRefundStore.autoRefundSinceUnits)
	const showExecuteSince = rmaAutoRefundStore.autoRefundExecute && rmaAutoRefundStore.autoRefundExecute !== AUTO_PROCESS_EXECUTE.IMMEDIATELY;
    
    const { getTranslation } = useDashboardTranslation();
    const pageScope = 'dashboard-create-rma';
    const autoRefundAllowed = currentPortalStore.isFeatureAllowed(FEATURES.AUTO_REFUND);
    const markAsDoneAllowed = currentPortalStore.isFeatureAllowed(FEATURES.MARK_AS_DONE);
    
	return (
		<div className='rma-automation-card'>
			<Card isExpandable={true}
			      toolbar={{title: <FontBody1 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{getTranslation('AUTOMATIONS', pageScope)}</FontBody1>}}>
                <FontBody2><DashTranslation translationKey={'DEFAULT_SETTINGS_IN_STORE_SETTINGS'} scope={pageScope} params={[config.apiUrl + '/dashboard/settings/store']} /></FontBody2>
				<div className={'flex-between automation-section'}>
					<div className={'automation-section__content'}>
						<FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>{getTranslation('ISSUE_REFUNDS', pageScope)}</FontBody1>
						<FontBody2 color={FONT_COLOR.NEUTRAL3}>{getTranslation('ISSUE_REFUNDS_AUTOMATION_DESCRIPTION', pageScope)}</FontBody2>
					</div>
					<Toggle label=""
					        checked={rmaAutoRefundStore.isOn && autoRefundAllowed}
					        onChange={rmaAutoRefundStore.setIsOn}
                            disabled={!autoRefundAllowed}
                    />
				</div>
				{rmaAutoRefundStore.isOn && autoRefundAllowed &&
					<div className='flex-line'>
						<div className='flex-line-item'>
							<FontBody2 color={FONT_COLOR.PRIMARY2}
							           variant={FONT_VARIANT.BOLD}>{getTranslation('WHEN', pageScope)}</FontBody2>
							<SelectDropdown
								menuWidth={DROPDOWN_MENU_WIDTH.MAX_CONTENT}
								value={autoProcessTriggerOptionsList().find(o => o.value === rmaAutoRefundStore.autoRefundTrigger)}
								options={autoProcessTriggerOptionsList()}
								onChange={(v) => rmaAutoRefundStore.setAutoRefundTrigger(v?.value as AUTO_PROCESS_TRIGGER)}
								required
								validationGroup={validationGroup}
							/>
							
							{rmaAutoRefundStore.autoRefundTrigger === AUTO_PROCESS_TRIGGER.ITEMS_VALIDATED &&
								<>
									<FontBody2 color={FONT_COLOR.PRIMARY2}
									           variant={FONT_VARIANT.BOLD}>{getTranslation('VALIDATED_IS', pageScope)}</FontBody2>
									<SelectDropdown
										value={autoProcessValidatedQualifierOptionsList().find(o => o.value === rmaAutoRefundStore.autoRefundValidatedQualifier)}
										options={autoProcessValidatedQualifierOptionsList()}
										onChange={(v) => rmaAutoRefundStore.setAutoRefundValidatedQualifier(v?.value as AUTO_PROCESS_VALIDATED_QUALIFIER)}
										required
										validationGroup={validationGroup}
									/>
								</>
							}
						</div>
						<div className='flex-line-item'>
							<FontBody2 color={FONT_COLOR.PRIMARY2}
							           variant={FONT_VARIANT.BOLD}> {getTranslation('EXECUTE', pageScope)} </FontBody2>
							<SelectDropdown
								value={autoProcessExecuteOptionsList().find(o => o.value === rmaAutoRefundStore.autoRefundExecute)}
								options={autoProcessExecuteOptionsList()}
								onChange={(v) => rmaAutoRefundStore.setAutoRefundExecute(v?.value as AUTO_PROCESS_EXECUTE)}
								required
								validationGroup={validationGroup}
							/>
							
							{showExecuteSince &&
								<div>
									<div className="execute-since-fields">
										<SelectDropdown
											value={autoProcessSinceValueOptions.find(o => o.value === rmaAutoRefundStore.autoRefundSinceValue)}
											options={autoProcessSinceValueOptions}
											onChange={(v) => rmaAutoRefundStore.setAutoRefundSinceValue(v?.value as number)}
											required
											validationGroup={validationGroup}
										/>
										
										<SelectDropdown
											value={sinceUnitOptionsList().find(o => o.value === rmaAutoRefundStore.autoRefundSinceUnits)}
											options={sinceUnitOptionsList()}
											onChange={(v) => rmaAutoRefundStore.setAutoRefundSinceUnit(v?.value as SINCE_UNIT)}
											required
											validationGroup={validationGroup}
										/>
									</div>
								</div>
							}
						</div>
					</div>
				}
				<div className={'flex-between automation-section'}>
					<div className={'automation-section__content'}>
						<FontBody1 variant={FONT_VARIANT.BOLD} color={FONT_COLOR.PRIMARY2}>{getTranslation('MARK_AS_DONE', pageScope)}</FontBody1>
						<FontBody2 color={FONT_COLOR.NEUTRAL3}>{getTranslation('MARK_AS_DONE_AUTOMATION_DESCRIPTION', pageScope)}</FontBody2>
					</div>
					<Toggle label=""
					        checked={rmaRequestStore.autoMarkAsDone && markAsDoneAllowed}
                            disabled={!markAsDoneAllowed}
					        onChange={rmaRequestStore.setAutoMarkAsDone}/>
				</div>
                <div className={'flex-end'}>
                    <Button leftIcon={IconResetToDefault} variant={BUTTON_VARIANTS.Transparent} disabled={rmaRequestStore.shouldDisableResetToDefault} text={getTranslation('RESET_TO_DEFAULT', pageScope)} onClick={rmaRequestStore.setDefaultsToAutomation}/>
                </div>
			</Card>
		</div>
	)
});

export default RMAAutomationCard;